/**
 * RWBR — the EZRA INJURY TABLES and the DRIVER / CREWMAN INJURY LOCATION chart.
 *
 * Two sheets, transcribed verbatim in `claude/RAW_ezra_appendix_b2_and_injuries.md`
 * off `EZRAS2RulesFinal.docx`'s embedded EMF images (`image8.png`, `image9.emf`).
 *
 * ── WHAT THE SHEETS PRINT, AND WHAT THEY DO NOT ────────────────────────────
 *
 * They print: a trigger, a severity table on APBA doubles, three severity
 * blocks, a 1d10 races-missed ladder, and a 36-row body-location chart.
 *
 * They do NOT print: any connection between the two charts, any mechanical
 * effect for any location, or anything at all about what a crewman injury does
 * despite the location sheet being titled *Driver / Crewman*. Three rulings
 * below close those gaps, and each says which way it went and why.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  D-EZ-16 — THE TRIGGER: a driver who AVOIDED the trouble cannot be hurt by it
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * The sheet's trigger line, verbatim and printed in red:
 *
 *   > *"Roll for injury anytime a driver fails their Escape roll on a Crash or
 *   >  a Wreck."*
 *
 * That names the FAILED ROLL. But two printed rules sit between a failed roll
 * and being in the incident, and both use the word *avoid*:
 *
 *   • RB/p.xiv, the performance chip — the driver "avoid[s] the trouble and
 *     hold[s] his position".
 *   • EZRA-S2 §4.0, the AW trait — "his awareness allows the Trouble to be
 *     avoided".
 *
 * A driver who demonstrably avoided a wreck cannot be injured in it; that state
 * is not a rules subtlety, it is unrepresentable. So the default reading is
 * **the injury check follows the car into the incident, not the die** — it
 * fires when the car is COLLECTED. EZRA's trigger names the failed roll because
 * in EZRA's own frame a failed roll IS being in the incident; the chip and AW
 * are the two mechanisms that retroactively take the car back out.
 *
 * The literal reading is a one-line toggle (`onFailedRoll`) and BOTH rates are
 * measured in `scripts/injuries.ts`, because this ruling changes how often a
 * driver gets hurt and that is exactly the kind of thing this project has been
 * bitten by before. Nothing here is silent.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  D-EZ-17 — THE LOCATION CHART IS FLAVOUR, AND IS ROLLED ANYWAY
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Howard's call, 16 Aug 2026. No location on the sheet carries a mechanical
 * effect and inventing one for each would be inventing 34 results. So a
 * location is rolled whenever an injury lands, carried on the event, and spoken
 * by the booth and the dossier — "out with a busted left wrist" — while
 * SEVERITY keeps every mechanic. The chart earns its place in the story layer
 * without acquiring rules it was never printed with.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  D-EZ-18 — ROWS 65 AND 66 ARE NOT LOCATIONS
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * `65` prints *"Re-roll Burns"* and `66` prints *"Re-roll Twice"*. Ruled as the
 * plain-English reading: **65 = burns, plus a re-roll to find where; 66 = roll
 * twice, and the driver carries two injuries.** Re-rolls repeat until they land
 * on a real location, so 65 → 65 → 42 is burns to the ribs and 66 → 66 is four
 * locations. Guarded against a pathological run by a re-roll cap that is
 * reported rather than swallowed.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  D-EZ-19 — A CREWMAN INJURY IS EXACTLY WHAT APPENDIX B2 SAYS, PLUS A PLACE
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Appendix B2's note * is the ONLY crewman mechanic anywhere in EZRA-S2 — *"Pit
 * crewman injured - All remaining Fast Pit checks in this race fail."* That is
 * already implemented in `pitTrouble.ts` and it is not touched here. This module
 * adds only the location roll on top, so the booth can say which crewman went
 * over the wall and where he got hit. No races-missed, no crew-grade change,
 * because nothing is printed for either.
 *
 * ── ONE TRANSCRIPTION ERROR IN OUR OWN RAW DOC, CORRECTED HERE ─────────────
 *
 * `RAW_ezra_appendix_b2_and_injuries.md` says in prose that **5/36 (13.9%)** of
 * failed CRASH escapes produce a Minor injury. Its own transcribed table says
 * otherwise: the CRASH column reads No, No, Minor, Minor, Minor, Minor across
 * 11-66, which is **4/36 (11.1%)**. The TABLE is implemented, the prose is
 * wrong, and `INJURY_RATES` states the closed form so the harness checks the
 * table rather than the sentence.
 */

import type { Dice } from './dice.ts';
import type { RuleEvent, DiceRecord, Modifier } from './ruleEvent.ts';

export const CITE_INJURY_TRIGGER =
  'EZRA INJURY TABLES (EZRAS2RulesFinal.docx image8.png) — "Roll for injury anytime a driver fails ' +
  'their Escape roll on a Crash or a Wreck."';
export const CITE_INJURY_SEVERITY =
  'EZRA INJURY TABLES — "ROLL APBA DICE" severity grid, CRASH and WRECK columns, doubles only; ' +
  '"All other results are No Injury".';
export const CITE_INJURY_MISSED =
  'EZRA INJURY TABLES — "ROLL 1D10" ladder, 1 = races next week through 10 = misses rest of season.';
export const CITE_INJURY_LOCATION =
  'DRIVER / CREWMAN INJURY LOCATION chart (EZRAS2RulesFinal.docx image9.emf), 11-66. D-EZ-17: ' +
  'flavour only — no location on the sheet carries a mechanical effect.';
export const CITE_D_EZ_16 =
  'D-EZ-16 — the injury check follows the car INTO the incident, not the die. A performance chip ' +
  '(RB/p.xiv) and the AW trait (EZRA-S2 §4.0) both print the word "avoid"; a driver who avoided ' +
  'the wreck cannot be injured in it. Literal reading available as `onFailedRoll`.';
export const CITE_D_EZ_18 =
  'D-EZ-18 — 65 "Re-roll Burns" = burns plus a location; 66 "Re-roll Twice" = two injuries. ' +
  'Neither reading is spelled out on the sheet.';
export const CITE_D_EZ_19 =
  'D-EZ-19 — a crewman injury is EZRA-S2 Appendix B2 note * ("All remaining Fast Pit checks in ' +
  'this race fail") and nothing more. The location roll is flavour on top.';

// ---------------------------------------------------------------------------
// Severity
// ---------------------------------------------------------------------------

export type InjurySeverity = 'none' | 'minor' | 'moderate' | 'serious';

/** The only two incident types that trigger an injury check. */
export type InjuryContext = 'CRASH' | 'WRECK';

/**
 * The severity grid, verbatim. Doubles only; the sheet closes with "All other
 * results are No Injury".
 *
 * ⚠ The MINOR block on the sheet describes *"a moderate injury"* — the sheet's
 * own copy-paste error, since the block is headed *Minor Injury* and the
 * Moderate block below repeats the same first sentence. Preserved verbatim in
 * `SEVERITY_TEXT`; not tidied.
 */
export const SEVERITY_GRID: Readonly<Record<string, Readonly<Record<InjuryContext, InjurySeverity>>>> =
  Object.freeze({
    '11': Object.freeze({ CRASH: 'none' as InjurySeverity, WRECK: 'none' as InjurySeverity }),
    '22': Object.freeze({ CRASH: 'none' as InjurySeverity, WRECK: 'minor' as InjurySeverity }),
    '33': Object.freeze({ CRASH: 'minor' as InjurySeverity, WRECK: 'minor' as InjurySeverity }),
    '44': Object.freeze({ CRASH: 'minor' as InjurySeverity, WRECK: 'moderate' as InjurySeverity }),
    '55': Object.freeze({ CRASH: 'minor' as InjurySeverity, WRECK: 'moderate' as InjurySeverity }),
    '66': Object.freeze({ CRASH: 'minor' as InjurySeverity, WRECK: 'serious' as InjurySeverity }),
  });

/** The three severity blocks, verbatim including "contiue". */
export const SEVERITY_TEXT: Readonly<Record<Exclude<InjurySeverity, 'none'>, string>> = Object.freeze({
  minor:
    'Driver has a moderate injury and cannot earn more than 1 Performance Chip at a time and cannot ' +
    'stockpile Performance Chips for the rest of the race.  The driver will race next week.',
  moderate:
    'Driver has a moderate injury and cannot earn more than 1 Performance Chip at a time and cannot ' +
    'stockpile Performance Chips for the rest of the race. Driver only has the stamina to complete ' +
    '20 more turns.  If the race is not over by that point, the driver retires due to injuries. ' +
    'The driver will race next week.',
  serious: 'Driver has a serious Injury and cannot contiue racing today.',
});

/** Moderate: "Driver only has the stamina to complete 20 more turns." */
export const MODERATE_STAMINA_TURNS = 20;

/** Minor and Moderate both: "cannot earn more than 1 Performance Chip at a time". */
export const INJURED_CHIP_CAP = 1;

export function severityFor(code: string, context: InjuryContext): InjurySeverity {
  const row = SEVERITY_GRID[code];
  // "All other results are No Injury" — every non-double.
  if (!row) return 'none';
  return row[context];
}

// ---------------------------------------------------------------------------
// How long he is out — 1d10
// ---------------------------------------------------------------------------

/**
 * "ROLL 1D10", verbatim. `'season'` is the sheet's "Misses Rest of Season";
 * 0 is its "Races Next week".
 */
export const RACES_MISSED: Readonly<Record<number, number | 'season'>> = Object.freeze({
  1: 0,
  2: 1,
  3: 1,
  4: 2,
  5: 2,
  6: 3,
  7: 4,
  8: 5,
  9: 6,
  10: 'season',
});

export const RACES_MISSED_TEXT: Readonly<Record<number, string>> = Object.freeze({
  1: 'Races Next week',
  2: 'Misses 1 race',
  3: 'Misses 1 race',
  4: 'Misses 2 races',
  5: 'Misses 2 Races',
  6: 'Misses 3 races',
  7: 'Misses 4 races',
  8: 'Misses 5 races',
  9: 'Misses 6 races',
  10: 'Misses Rest of Season',
});

// ---------------------------------------------------------------------------
// Where he got hurt — the 11-66 location chart
// ---------------------------------------------------------------------------

/**
 * All 36 rows, verbatim — including row 21's printed "Collarebone", which row
 * 22 spells correctly. Rows 65 and 66 are not locations; see D-EZ-18.
 */
export const INJURY_LOCATIONS: Readonly<Record<string, string>> = Object.freeze({
  '11': 'Concussion',
  '12': 'Left Eye',
  '13': 'Right Eye',
  '14': 'Ear/Balance',
  '15': 'Teeth/Jaw',
  '16': 'Neck',
  '21': 'Left Collarebone',
  '22': 'Right Collarbone',
  '23': 'Left Shoulder',
  '24': 'Right Shoulder',
  '25': 'Left Arm',
  '26': 'Right Arm',
  '31': 'Left Elbow',
  '32': 'Right Elbow',
  '33': 'Left Wrist',
  '34': 'Right Wrist',
  '35': 'Left Hand',
  '36': 'Right Hand',
  '41': 'Fingers/Thumb',
  '42': 'Ribs',
  '43': 'Internal Injuries',
  '44': 'Back',
  '45': 'Left Hip',
  '46': 'Right Hip',
  '51': 'Left Leg',
  '52': 'Right Leg',
  '53': 'Left Knee',
  '54': 'Right Knee',
  '55': 'Left Ankle',
  '56': 'Right Ankle',
  '61': 'Left Foot',
  '62': 'Right Foot',
  '63': 'Sternum',
  '64': 'Tailbone',
  '65': 'Re-roll Burns',
  '66': 'Re-roll Twice',
});

export const LOCATION_REROLL_BURNS = '65';
export const LOCATION_REROLL_TWICE = '66';

/**
 * A guard on D-EZ-18's recursion, not a rule. 65 and 66 are 2/36 each, so a run
 * long enough to hit this has probability under 1e-15; if it ever fires, the
 * count comes out on the result rather than being swallowed.
 */
export const MAX_LOCATION_REROLLS = 12;

export interface InjuryLocation {
  /** Every code rolled, in order, including the 65s and 66s that re-rolled. */
  codes: string[];
  /** The real body locations landed on. Two or more when 66 came up. */
  locations: string[];
  /** True when a 65 was rolled — burns, in addition to the location(s). */
  burns: boolean;
  /** Set only if the re-roll guard tripped. Reported, never swallowed. */
  rerollCapHit?: boolean;
}

/**
 * Roll the location chart, honouring D-EZ-18.
 *
 * 65 sets `burns` and re-rolls for a place. 66 rolls twice, and each of those
 * two rolls is itself subject to 65 and 66 — the sheet gives no reason to stop
 * the rule applying to its own results.
 */
export function rollInjuryLocation(dice: Dice, records?: DiceRecord[]): InjuryLocation {
  const out: InjuryLocation = { codes: [], locations: [], burns: false };
  let budget = MAX_LOCATION_REROLLS;
  // A queue of "locations still owed", so 66 can add one without recursion.
  let owed = 1;

  while (owed > 0) {
    if (budget-- <= 0) {
      out.rerollCapHit = true;
      break;
    }
    owed--;
    const roll = dice.code1166();
    out.codes.push(roll.code);
    records?.push({
      kind: '11-66',
      dice: [roll.red, roll.white],
      code: roll.code,
      label: 'INJURY LOCATION',
      faces: { red: roll.red, white: roll.white },
    });
    if (roll.code === LOCATION_REROLL_BURNS) {
      out.burns = true;
      owed += 1; // burns, plus a place
      continue;
    }
    if (roll.code === LOCATION_REROLL_TWICE) {
      owed += 2; // two injuries
      continue;
    }
    out.locations.push(INJURY_LOCATIONS[roll.code]);
  }

  return out;
}

// ---------------------------------------------------------------------------
// The result
// ---------------------------------------------------------------------------

export interface InjuryResult extends RuleEvent {
  eventType: 'injury';
  momentTag: 'injury.none' | 'injury.minor' | 'injury.moderate' | 'injury.serious';
  driverId: string;
  context: InjuryContext;
  severity: InjurySeverity;
  /** The APBA code rolled for severity. */
  severityCode: string;
  /** Serious only — the 1d10 face and what it means. */
  missedRoll: number | null;
  racesMissed: number | 'season' | null;
  /** Null when no injury resulted; D-EZ-17 rolls it only on a real injury. */
  location: InjuryLocation | null;
  /** Moderate only — the turn after which he retires from this race. */
  staminaUntilTurn: number | null;
  /** True when the driver cannot continue in this race (Serious). */
  outOfRace: boolean;
  /** Verbatim severity block, so nothing is paraphrased at the player. */
  severityText: string | null;
}

export interface InjuryInput {
  driverId: string;
  name?: string;
  context: InjuryContext;
  dice: Dice;
  /** The turn the incident happened on, for the Moderate stamina clause. */
  turn?: number;
}

/**
 * Roll the EZRA injury check for one driver in one incident.
 *
 * The caller decides WHETHER to call this — see D-EZ-16 for what the trigger
 * actually is. This function only knows how to read the sheets.
 */
export function rollInjury(input: InjuryInput): InjuryResult {
  const who = input.name ?? input.driverId;
  const diceRecords: DiceRecord[] = [];
  const modifiers: Modifier[] = [
    { name: 'incident type', value: input.context, detail: CITE_INJURY_TRIGGER },
  ];

  const sev = input.dice.code1166();
  diceRecords.push({
    kind: '11-66',
    dice: [sev.red, sev.white],
    code: sev.code,
    label: 'INJURY SEVERITY (APBA)',
    faces: { red: sev.red, white: sev.white },
    flags: { doubles: sev.doubles },
  });
  const severity = severityFor(sev.code, input.context);

  if (severity === 'none') {
    return {
      eventType: 'injury',
      momentTag: 'injury.none',
      ruleCitation: CITE_INJURY_SEVERITY,
      actorIds: [input.driverId],
      positionDeltas: [],
      dice: diceRecords,
      modifiers,
      description: `${who} climbs out under his own power — ${sev.code} on the APBA dice, no injury.`,
      driverId: input.driverId,
      context: input.context,
      severity: 'none',
      severityCode: sev.code,
      missedRoll: null,
      racesMissed: null,
      location: null,
      staminaUntilTurn: null,
      outOfRace: false,
      severityText: null,
    };
  }

  // D-EZ-17 — a location, for the story, on every real injury.
  const location = rollInjuryLocation(input.dice, diceRecords);
  modifiers.push({
    name: 'injury location',
    value: location.locations.join(' + ') || '(unresolved)',
    detail: CITE_INJURY_LOCATION,
  });
  if (location.burns) modifiers.push({ name: 'burns', value: 'yes', detail: CITE_D_EZ_18 });

  let missedRoll: number | null = null;
  let racesMissed: number | 'season' | null = null;
  if (severity === 'serious') {
    missedRoll = input.dice.d10();
    diceRecords.push({ kind: 'd10', dice: [missedRoll], total: missedRoll, label: 'RACES MISSED' });
    racesMissed = RACES_MISSED[missedRoll];
    modifiers.push({
      name: 'races missed',
      value: RACES_MISSED_TEXT[missedRoll],
      detail: CITE_INJURY_MISSED,
    });
  }

  const staminaUntilTurn =
    severity === 'moderate' && input.turn !== undefined
      ? input.turn + MODERATE_STAMINA_TURNS
      : null;

  const place = location.locations.length > 0 ? location.locations.join(' and ') : 'unspecified';
  const burnt = location.burns ? 'burns and ' : '';
  const tail =
    severity === 'serious'
      ? ` He is done for the day — ${RACES_MISSED_TEXT[missedRoll as number].toLowerCase()}.`
      : severity === 'moderate'
        ? ` He can hold on for ${MODERATE_STAMINA_TURNS} more turns, and no longer.`
        : ' He stays in it, but he cannot stockpile chips for the rest of the day.';

  return {
    eventType: 'injury',
    momentTag: `injury.${severity}` as InjuryResult['momentTag'],
    ruleCitation: `${CITE_INJURY_SEVERITY}; ${CITE_INJURY_LOCATION}`,
    actorIds: [input.driverId],
    positionDeltas: [],
    dice: diceRecords,
    modifiers,
    description:
      `${who} is hurt in the ${input.context.toLowerCase()} — ${sev.code} on the APBA dice, ` +
      `${severity} injury, ${burnt}${place}.${tail}`,
    driverId: input.driverId,
    context: input.context,
    severity,
    severityCode: sev.code,
    missedRoll,
    racesMissed,
    location,
    staminaUntilTurn,
    outOfRace: severity === 'serious',
    severityText: SEVERITY_TEXT[severity],
  };
}

// ---------------------------------------------------------------------------
// Closed form — what the harness checks against
// ---------------------------------------------------------------------------

/**
 * Straight off the grid, per injury CHECK (not per race, not per incident).
 *
 * These are the numbers `scripts/injuries.ts` measures against. They are stated
 * here rather than derived in the script so a change to the grid that nobody
 * meant breaks the comparison instead of quietly moving the target.
 *
 * Note the CRASH minor rate: **4/36**, not the 5/36 the RAW doc's prose claims.
 * Count the column — No, No, Minor, Minor, Minor, Minor.
 */
export const INJURY_RATES = Object.freeze({
  CRASH: Object.freeze({ none: 32 / 36, minor: 4 / 36, moderate: 0, serious: 0 }),
  WRECK: Object.freeze({ none: 31 / 36, minor: 2 / 36, moderate: 2 / 36, serious: 1 / 36 }),
});

/** P(a location roll shows burns at least once) and P(two or more locations). */
export const LOCATION_RATES = Object.freeze({
  burns: 1 / 36,
  rerollTwice: 1 / 36,
});
